import { calculateCartItemAmounts } from './cart'

/**
 * Test whether an order amount satisfies a promotion condition.
 *
 * @param {number} amount Final invoice amount after VAT.
 * @param {Object} condition Promotion condition.
 * @returns {boolean} Whether the condition is satisfied.
 */
export const matchesPromotionCondition = (amount, condition) => {
  const from = Number(condition.amount_from || 0)
  const to = condition.amount_to === null || condition.amount_to === undefined
    ? null
    : Number(condition.amount_to)

  if (condition.operator_sign === 'between') {
    return amount >= from && to !== null && amount <= to
  }

  if (condition.operator_sign === '>=') return amount >= from
  if (condition.operator_sign === '>') return amount > from
  if (condition.operator_sign === '<=') return amount <= from
  if (condition.operator_sign === '<') return amount < from

  // The promotion editor calls this operator "Equal", but its business
  // meaning is a reached threshold rather than an exact invoice total.
  return amount >= from
}

/**
 * Select one applicable condition per promotion, preferring the highest tier.
 *
 * @param {number} amount Final invoice amount after VAT.
 * @param {Object} promotion Active promotion.
 * @returns {Object|null} Applicable condition.
 */
export const findApplicableCondition = (amount, promotion) => {
  const matched = (promotion.conditions || [])
    .filter(condition => matchesPromotionCondition(amount, condition))
    .sort((left, right) => Number(right.amount_from || 0) - Number(left.amount_from || 0))

  return matched[0] || null
}

/**
 * Build free lines for one buy-item/free-item condition.
 *
 * @param {Array} paidItems Paid cart lines.
 * @param {Object} promotion Active type-4 promotion.
 * @param {Object} condition Buy/free condition.
 * @param {Object} quantities User-adjusted promotion quantities.
 * @returns {Array} Generated type-4 free-item lines.
 */
const calculateBuyItemFreeItems = (paidItems, promotion, condition, quantities) => {
  const buySaleCodeId = condition.buy_item && condition.buy_item.pro_sale_code_id
  const buyQuantity = Number(condition.buy_quantity || 0)
  if (!buySaleCodeId || buyQuantity <= 0) return []

  const boughtItems = paidItems.filter(
    item => String(item.pro_sale_code_id) === String(buySaleCodeId)
  )
  const boughtQuantity = boughtItems.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  )
  const eligibleSets = Math.floor(boughtQuantity / buyQuantity)
  if (eligibleSets < 1) return []

  const sourceItem = boughtItems[0]
  const freeItems = []

  ;(condition.items || []).forEach(item => {
    const configuredQuantity = Number(item.quantity || 0) * eligibleSets
    const cartItemKey = `promotion-${promotion.promotion_id}-4-${condition.promo_free_item_cond_id}-${item.pro_sale_code_id}`
    const quantity = Number(quantities[cartItemKey] || configuredQuantity)
    if (!item.pro_sale_code_id || quantity <= 0) return

    const freeItem = {
      cart_item_key: cartItemKey,
      product_id: item.product_id,
      uom_id: item.uom_id,
      name: item.product_name_en,
      nameKh: item.product_name_kh,
      sale_code: item.sale_code,
      unit_price: 0,
      quantity,
      discount_type: '%',
      discount_rate: 0,
      discount_amount: 0,
      total_after_discount: 0,
      vat_rate: 0,
      vat_amount: 0,
      grand_total: 0,
      stock_out_quantity: quantity,
      remarks: null,
      display_order: null,
      pro_sale_code_id: item.pro_sale_code_id,
      promotion,
      promo_type_id: 4,
      promo_free_item_cond_id: condition.promo_free_item_cond_id,
      is_free_promotion_item: true,
      is_allow_adjust_free_quantity: Boolean(item.is_allow_adjust_free_quantity),
      ref_promo_product_id: sourceItem.product_id
    }

    freeItems.push({ ...freeItem, ...calculateCartItemAmounts(freeItem) })
  })

  return freeItems
}

/**
 * Build free stock-out lines for the current paid cart.
 *
 * @param {Array} paidItems Cart items excluding generated free lines.
 * @param {number} invoiceAmount Final invoice amount after VAT.
 * @param {Array} promotions Active promotion definitions.
 * @returns {Array} Generated type-2 free-item cart lines.
 */
export const calculatePromotionItems = (
  paidItems,
  promotions,
  invoiceAmount,
  selections = {},
  quantities = {}
) => {
  const qualifyingAmount = Number.isFinite(Number(invoiceAmount))
    ? Number(invoiceAmount)
    : paidItems.reduce(
        (total, item) => total + Number(item.total_after_discount || 0),
        0
      )
  const freeItems = []

  ;(promotions || []).forEach(promotion => {
    if (Number(promotion.promo_type_id) === 4) {
      ;(promotion.conditions || []).forEach(condition => {
        freeItems.push(
          ...calculateBuyItemFreeItems(paidItems, promotion, condition, quantities)
        )
      })
      return
    }

    if (Number(promotion.promo_type_id) !== 2) return

    const condition = findApplicableCondition(qualifyingAmount, promotion)
    if (!condition) return

    const selectedItemId = selections[condition.promo_free_item_cond_id]
    const conditionItems = String(condition.free_status).toLowerCase() === 'any'
      ? (condition.items || []).filter(item => String(item.pro_sale_code_id) === String(selectedItemId))
      : condition.items || []

    conditionItems.forEach(item => {
      const cartItemKey = `promotion-${promotion.promotion_id}-${condition.promo_free_item_cond_id}-${item.pro_sale_code_id}`
      const quantity = Number(quantities[cartItemKey] || item.quantity || 0)
      const freeItem = {
        cart_item_key: cartItemKey,
        product_id: item.product_id,
        uom_id: item.uom_id,
        name: item.product_name_en,
        nameKh: item.product_name_kh,
        sale_code: item.sale_code,
        unit_price: 0,
        quantity,
        discount_type: '%',
        discount_rate: 0,
        discount_amount: 0,
        total_after_discount: 0,
        vat_rate: 0,
        vat_amount: 0,
        grand_total: 0,
        stock_out_quantity: quantity,
        remarks: null,
        display_order: null,
        pro_sale_code_id: item.pro_sale_code_id,
        promotion: promotion,
        promo_type_id: 2,
        promo_free_item_cond_id: condition.promo_free_item_cond_id,
        is_free_promotion_item: true,
        is_allow_adjust_free_quantity: Boolean(item.is_allow_adjust_free_quantity),
        ref_promo_product_id: null
      }

      freeItems.push({ ...freeItem, ...calculateCartItemAmounts(freeItem) })
    })
  })

  return freeItems
}

/**
 * Replace generated promotion lines after every cart change.
 *
 * @param {Object} order Retail order.
 * @param {Array} promotions Active promotion definitions.
 * @returns {Array} Complete cart lines.
 */
export const getPromotionQualifyingAmount = order => {
  const paidItems = ((order && order.cartItems) || []).filter(
    item => !item.is_free_promotion_item
  )
  const itemTotalAfterDiscount = paidItems.reduce(
    (total, item) => total + Number(item.total_after_discount || 0),
    0
  )
  const vatAmount = itemTotalAfterDiscount * (Number((order && order.vat_rate) || 0) / 100)

  const invoiceDiscount = Math.min(
    Math.max(Number((order && order.invoice_discount_amount) || 0), 0),
    itemTotalAfterDiscount + vatAmount
  )

  return Math.max(itemTotalAfterDiscount + vatAmount - invoiceDiscount, 0)
}

/**
 * Get selectable free-item choices for matched Any conditions.
 *
 * @param {Object} order Retail order.
 * @param {Array} promotions Active promotion definitions.
 * @returns {Array} Selectable promotion choices.
 */
export const getPromotionChoices = (order, promotions) => {
  const amount = getPromotionQualifyingAmount(order)

  return (promotions || []).reduce((choices, promotion) => {
    if (Number(promotion.promo_type_id) !== 2) return choices

    const condition = findApplicableCondition(amount, promotion)
    if (!condition || String(condition.free_status).toLowerCase() !== 'any') return choices

    choices.push({
      promotion_id: promotion.promotion_id,
      promotion_name: promotion.promotion_name,
      condition_id: condition.promo_free_item_cond_id,
      items: condition.items || []
    })

    return choices
  }, [])
}

export const applyOrderPromotions = (order, promotions) => {
  const paidItems = ((order && order.cartItems) || []).filter(
    item => !item.is_free_promotion_item
  )
  const freeItems = calculatePromotionItems(
    paidItems,
    promotions,
    getPromotionQualifyingAmount(order),
    order && order.promotion_selections,
    order && order.promotion_quantities
  )

  return paidItems.concat(freeItems)
}
